// Topbar — CRM header strip.
//
// Layout: [workspace name] .......... [avatar+email] [logout].
//
// Logout contract:
//   1. POST /auth/logout (server clears the session cookie)
//   2. analytics.reset() clears in-memory buffers + dedupe state
//   3. useScopeStore.clear() drops user + scope from the Zustand store
//   4. navigate('/login', { replace: true })
//
// Note on why we call `analytics.reset()` BEFORE `navigate()`: the next
// route change would emit a `page` event with stale identity if we reset
// after navigation.

import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Bot, PanelRightClose, PanelRightOpen, Search, Settings } from 'lucide-react';

import { Button, Input, Tag } from 'antd';
import { useAnalytics } from '@govard/analytics';
import { useTranslation } from '@govard/i18n-core/react';

import { ApiError, apiPost } from '../../shared/api-client/index.js';
import {
  useCurrentScope,
  useCurrentUser,
  useScopeStore,
} from '../../shared/scope/useCurrentScope.js';

interface TopbarProps {
  rightSidebarOpen?: boolean;
  onToggleRightSidebar?: () => void;
}

export function Topbar({
  rightSidebarOpen = false,
  onToggleRightSidebar,
}: TopbarProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const user = useCurrentUser();
  const scope = useCurrentScope();
  const clear = useScopeStore((state) => state.clear);

  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workspaceLabel = scope?.workspaceId ?? t('crm.topbar.workspace_fallback');
  const userEmail = user?.email ?? '';
  const userInitial = userEmail.length > 0 ? userEmail[0]!.toUpperCase() : '?';

  const onLogout = async (): Promise<void> => {
    setLoggingOut(true);
    setError(null);
    try {
      await apiPost<unknown, Record<string, never>>('/auth/logout', {});
      analytics.reset();
      clear();
      navigate('/login', { replace: true });
    } catch (err) {
      // 401 means the cookie was already invalid — same user-visible result.
      if (err instanceof ApiError && err.status === 401) {
        analytics.reset();
        clear();
        navigate('/login', { replace: true });
        return;
      }
      setError(t('crm.topbar.logout_error'));
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <header
      className="relative z-10 flex shrink-0 items-center justify-between border-b"
      data-testid="crm-topbar"
      style={{
        height: '64px',
        backgroundColor: '#fffdfa',
        borderBottom: '1px solid #e7e5e4',
        padding: '0 40px',
      }}
    >
      <div className="flex-1" style={{ maxWidth: 420 }}>
        <Input
          placeholder="Search across OS..."
          prefix={<Search size={16} strokeWidth={1.5} style={{ color: '#a8a29e', marginRight: 4 }} />}
          suffix={<kbd style={{ fontSize: 10, padding: '2px 6px', background: '#f5f5f4', borderRadius: 4, color: '#78716c', border: '1px solid #e7e5e4' }}>⌘K</kbd>}
          style={{ 
            backgroundColor: '#f5f5f4', 
            border: '1px solid transparent',
            outline: 'none',
            boxShadow: 'none',
            fontSize: '14px',
            color: '#44403c',
            transition: 'background-color 0.2s, border-color 0.2s',
          }}
          className="search-input-hover"
          onFocus={(e) => {
            e.target.style.backgroundColor = '#fff';
            e.target.style.borderColor = '#d97706';
          }}
          onBlur={(e) => {
            e.target.style.backgroundColor = '#f5f5f4';
            e.target.style.borderColor = 'transparent';
          }}
        />
      </div>

      <div
        className="flex items-center"
        aria-label={t('crm.topbar.operator_status')}
        role="group"
        style={{ gap: 8, marginLeft: 16 }}
      >
        <Tag color="success" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 6, margin: 0 }}>
          <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] opacity-80">{t('crm.topbar.llm_gate')}</span>
          <span className="text-xs font-medium ml-1">{t('crm.topbar.status_online')}</span>
        </Tag>
        <Tag color="blue" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 6, margin: 0 }}>
          <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] opacity-80">{t('crm.topbar.scope_label')}</span>
          <span className="text-xs font-medium ml-1">{workspaceLabel}</span>
        </Tag>
      </div>

      <div
        className="flex items-center border-l"
        aria-label={t('crm.topbar.user_menu')}
        role="group"
        style={{
          gap: 12,
          paddingLeft: 20,
          marginLeft: 20,
          borderColor: '#e7e5e4',
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          <Button
            type="text"
            icon={rightSidebarOpen ? <PanelRightClose size={18} strokeWidth={1.5} /> : <PanelRightOpen size={18} strokeWidth={1.5} />}
            onClick={onToggleRightSidebar}
            aria-pressed={rightSidebarOpen}
            aria-label={t('crm.topbar.toggle_context')}
            title={t('crm.topbar.toggle_context')}
            data-testid="crm-topbar-toggle-context"
            style={{ color: '#78716c' }}
          />
          <Button
            type="text"
            icon={<Bot size={18} strokeWidth={1.5} />}
            title={t('crm.topbar.system_agent')}
            aria-label={t('crm.topbar.system_agent')}
            style={{ color: '#78716c' }}
          />
          <Button
            type="text"
            icon={<Bell size={18} strokeWidth={1.5} />}
            style={{ color: '#78716c' }}
          />
          <Button
            type="text"
            icon={<Settings size={18} strokeWidth={1.5} />}
            style={{ color: '#78716c' }}
          />
        </div>
        
        <div className="flex items-center gap-3 ml-2">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
              style={{ backgroundColor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}
            >
              {userInitial}
            </span>
            <span className="hidden text-[13px] font-medium sm:inline" style={{ color: '#44403c' }}>{userEmail}</span>
          </div>
          <Button
            onClick={() => {
              void onLogout();
            }}
            loading={loggingOut}
            data-testid="crm-topbar-logout"
            size="small"
            style={{ fontSize: 13 }}
          >
            {t('crm.topbar.logout')}
          </Button>
        </div>
      </div>

      {error !== null ? (
        <p role="alert" className="sr-only">
          {error}
        </p>
      ) : null}
    </header>
  );
}
